import React, { useState, useContext, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import DispatchContext from '../DispatchContext'
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js"
import ApplePayButton from './ApplePayButton'
import './V3Register.css'

// Planurile de subscripție (selectate în funcție de modul din backend)
const SANDBOX_PLANS = {
  plus: "P-4J575883JS6144305NCYVJLQ",
  premium: "P-0GW471494K971462BNCYVITI",
}
const LIVE_PLANS = {
  plus: "P-3R772572SY047960TNCYUYXI",
  premium: "P-4TV28343F2894283NNCYU3ZI",
}

const API_BASE = "https://backend.alephmedia.ro/api"

// Lista de județe/sectoare pentru dropdown
const JUDETE_SECTOARE = [
  'Sectorul 1', 'Sectorul 2', 'Sectorul 3', 'Sectorul 4', 'Sectorul 5', 'Sectorul 6',
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 'Brăila',
  'Brașov', 'Buzău', 'Călărași', 'Caraș-Severin', 'Cluj', 'Constanța', 'Covasna',
  'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj', 'Harghita', 'Hunedoara', 'Ialomița',
  'Iași', 'Ilfov', 'Maramureș', 'Mehedinți', 'Mureș', 'Neamț', 'Olt', 'Prahova',
  'Sălaj', 'Satu Mare', 'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea', 'Vâlcea',
  'Vaslui', 'Vrancea'
]

// Lista de domenii de activitate
const DOMENII_ACTIVITATE = [
  { value: '', label: '-' },
  { value: 'Administratie Publica: Local', label: 'Administratie Publica: Local' },
  { value: 'Administratie Publica: National', label: 'Administratie Publica: National' },
  { value: 'Agricultura', label: 'Agricultura' },
  { value: 'Asigurari', label: 'Asigurari' },
  { value: 'Auto si Aerospatial', label: 'Auto si Aerospatial' },
  { value: 'Bancar', label: 'Bancar' },
  { value: 'Chimice', label: 'Chimice' },
  { value: 'Comert en-detail', label: 'Comert en-detail' },
  { value: 'Comert en-gross', label: 'Comert en-gross' },
  { value: 'Constructii', label: 'Constructii' },
  { value: 'Consultanta', label: 'Consultanta' },
  { value: 'Divertisment', label: 'Divertisment' },
  { value: 'Educatie', label: 'Educatie' },
  { value: 'FMCG', label: 'FMCG' },
  { value: 'High-Tech', label: 'High-Tech' },
  { value: 'Imobiliare', label: 'Imobiliare' },
  { value: 'Ind Farmaceutica', label: 'Ind Farmaceutica' },
  { value: 'Ind. Petroliera si Minerit', label: 'Ind. Petroliera si Minerit' },
  { value: 'Ind. Prelucratoare', label: 'Ind. Prelucratoare' },
  { value: 'Juridic', label: 'Juridic' },
  { value: 'Media', label: 'Media' },
  { value: 'ONG', label: 'ONG' },
  { value: 'Proiectare', label: 'Proiectare' },
  { value: 'Publishing', label: 'Publishing' },
  { value: 'Sanatate', label: 'Sanatate' },
  { value: 'Servicii Financiare', label: 'Servicii Financiare' },
  { value: 'Telecomunicatii', label: 'Telecomunicatii' },
  { value: 'Transport', label: 'Transport' },
  { value: 'Utilitati', label: 'Utilitati' },
]

const V3Register = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useContext(DispatchContext)
  
  // Câmpuri obligatorii (pentru compatibilitate cu RegisterRequest din app-ul mobil)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [c_password, setCPassword] = useState('')
  
  // Câmpuri opționale (pentru compatibilitate cu app-ul mobil care nu le-a implementat încă)
  const [phone, setPhone] = useState('')
  const [compania, setCompania] = useState('')
  const [judetSector, setJudetSector] = useState('Sectorul 1')
  const [localitate, setLocalitate] = useState('')
  const [adresa, setAdresa] = useState('')
  const [domeniuActivitate, setDomeniuActivitate] = useState('')
  const [functia, setFunctia] = useState('')
  
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [registered, setRegistered] = useState(false)
  const [selected, setSelected] = useState('plus') // 'plus' sau 'premium'
  const [paypalConfig, setPaypalConfig] = useState({ mode: 'sandbox', clientId: null })
  const [isGuestTimeout, setIsGuestTimeout] = useState(false)

  // Verifică dacă utilizatorul a fost redirecționat din cauza timeout-ului
  useEffect(() => {
    const guestTimeout = localStorage.getItem('v3_guest_timeout')
    if (guestTimeout === 'true') {
      setIsGuestTimeout(true)
    }
  }, [])

  // Preselectare plan din query param (ex: /v3.0/register?plan=premium)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const plan = params.get('plan')
    if (plan === 'plus' || plan === 'premium') {
      setSelected(plan)
    }
  }, [location.search])

  // Preluăm clientId și modul PayPal din backend ca să evităm mismatch de cont/mediu
  useEffect(() => {
    async function loadConfig() {
      try {
        const res = await fetch(`${API_BASE}/payment/paypal/config`)
        const json = await res.json()
        if (json.success && json.data?.clientId) {
          setPaypalConfig({ mode: json.data.mode || 'sandbox', clientId: json.data.clientId })
        }
      } catch (e) {
        console.error('Eroare la încărcarea config PayPal:', e)
      }
    }
    loadConfig()
  }, [])

  // Construim numele complet din prenume și nume
  const name = `${firstName} ${lastName}`.trim()

  const handleNext = (event) => {
    event.preventDefault()
    setError(null)
    // Oprim timerul global pe durata procesului de înregistrare/plată
    dispatch({ type: 'stopTimer' })

    // Validare câmpuri obligatorii (doar cele necesare pentru compatibilitate cu app-ul mobil)
    if (!firstName || !lastName || !email || !password || !c_password) {
      setError('Completați numele, emailul și parola pentru a continua')
      return
    }
    if (password !== c_password) {
      setError('Parolele nu se potrivesc')
      return
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Email invalid')
      return
    }
    if (password.length < 6) {
      setError('Parola trebuie să aibă minim 6 caractere')
      return
    }
    setRegistered(true)
  }

  if (registered) {
    return (
      <div className="v3-register-root">
        <div className="v3-register-container">
          <div className="v3-register-header">
            <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" className="v3-register-logo" />
            <h1>Alegeți abonamentul</h1>
            <p>Selectați planul care vi se potrivește</p>
            <p>
              <a href={`${API_BASE}/auth/keycloak/redirect`} className="v3-register-alephid">
                Ai deja Aleph ID? Intră cu Aleph ID
              </a>
            </p>
          </div>

          <div className="v3-register-plans">
            <div
              className={`v3-register-plan${selected === 'plus' ? ' selected' : ''}`}
              onClick={() => setSelected('plus')}
              role="button"
              tabIndex={0}
            >
              <h3>Abonament Plus</h3>
              <p className="v3-register-price">4.99€ / lună</p>
              <ul>
                <li>Acces la știrile de bază</li>
                <li>Notificări importante</li>
                <li>Conținut exclusiv limitat</li>
              </ul>
              {selected === 'plus' && <div className="v3-register-checkmark">✔</div>}
            </div>

            <div
              className={`v3-register-plan${selected === 'premium' ? ' selected' : ''}`}
              onClick={() => setSelected('premium')}
              role="button"
              tabIndex={0}
            >
              <h3>Abonament Premium</h3>
              <p className="v3-register-price">9.99€ / lună</p>
              <ul>
                <li>Acces nelimitat la tot conținutul</li>
                <li>Conținut exclusiv premium</li>
                <li>Suport prioritar</li>
              </ul>
              {selected === 'premium' && <div className="v3-register-checkmark">✔</div>}
            </div>
          </div>

          {error && (
            <div className="v3-register-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <div className="v3-register-apple-pay">
            <ApplePayButton
              amount={selected === 'premium' ? 999 : 499}
              currency="ron"
              label={`Abonament ${selected === 'premium' ? 'Premium' : 'Plus'}`}
              onSuccess={async (paymentMethod) => {
                try {
                  const response = await fetch(`${API_BASE}/payment/paypal/save-subscription`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      email,
                      name,
                      first_name: firstName,
                      last_name: lastName,
                      phone,
                      password,
                      judet_sector: judetSector,
                      localitate,
                      adresa,
                      compania,
                      domeniu_activitate: domeniuActivitate,
                      functia,
                      subscription_id: paymentMethod.id,
                      level: selected,
                      payment_method: 'apple_pay'
                    })
                  })
                  
                  const result = await response.json()
                  if (result.success) {
                    // Ștergem flag-ul de guest timeout
                    localStorage.removeItem('v3_guest_timeout')
                    dispatch({ type: 'resetTimer' })
                    localStorage.setItem('authToken', result.data.token)
                    localStorage.setItem('userData', JSON.stringify({
                      name: result.data.name,
                      email: result.data.email,
                      subscription_type: result.data.subscription_type,
                      subscription_status: result.data.subscription_status,
                      subscription_expires_at: result.data.subscription_expires_at
                    }))
                    dispatch({ type: "userLoggedIn", payload: {
                      name: result.data.name,
                      email: result.data.email,
                      subscription_type: result.data.subscription_type,
                      subscription_status: result.data.subscription_status,
                      subscription_expires_at: result.data.subscription_expires_at
                    }})
                    dispatch({
                      type: "newEvent",
                      value: {
                        header: "Abonament activat",
                        message: "Plata a fost procesată cu succes!",
                        link: "/v3.0"
                      }
                    })
                    navigate('/v3.0')
                    window.location.reload()
                  } else {
                    throw new Error(result.message || 'Eroare la salvarea subscripției')
                  }
                } catch (e) {
                  console.error('Eroare la salvarea subscripției:', e)
                  setError('Eroare la salvarea subscripției. Vă rugăm să contactați suportul.')
                  dispatch({ type: 'resetTimer' })
                }
              }}
              onError={(err) => {
                setError('Eroare la procesarea plății Apple Pay: ' + (err.message || ''))
              }}
            />
          </div>

          <div className="v3-register-separator">SAU</div>

          <div className="v3-register-paypal">
            {paypalConfig.clientId && (
              <PayPalScriptProvider options={{ "client-id": paypalConfig.clientId, vault: true, locale: "ro_RO", intent: "subscription" }}>
                <PayPalButtons
                  style={{ layout: "vertical" }}
                  createSubscription={(data, actions) => {
                    const planId = (paypalConfig.mode === 'sandbox' ? SANDBOX_PLANS : LIVE_PLANS)[selected]
                    return actions.subscription.create({
                      plan_id: planId
                    })
                  }}
                  onApprove={async (data, actions) => {
                    // Salvează datele în localStorage, inclusiv nivelul subscripției
                    localStorage.setItem('subscriptionLevel', selected)
                    localStorage.setItem('subscriptionId', data.subscriptionID)
                    
                    // Trimite la backend pentru a salva subscripția la user
                    try {
                      const response = await fetch(`${API_BASE}/payment/paypal/save-subscription`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          email,
                          name,
                          first_name: firstName,
                          last_name: lastName,
                          phone,
                          password,
                          judet_sector: judetSector,
                          localitate,
                          adresa,
                          compania,
                          domeniu_activitate: domeniuActivitate,
                          functia,
                          subscription_id: data.subscriptionID,
                          level: selected
                        })
                      })
                      
                      const result = await response.json()
                      if (result.success) {
                        // Ștergem flag-ul de guest timeout
                        localStorage.removeItem('v3_guest_timeout')
                        // Repornim timerul după finalizare
                        dispatch({ type: 'resetTimer' })
                        // Salvăm toate datele relevante în localStorage
                        localStorage.setItem('authToken', result.data.token)
                        localStorage.setItem('userData', JSON.stringify({
                          name: result.data.name,
                          email: result.data.email,
                          subscription_type: result.data.subscription_type,
                          subscription_status: result.data.subscription_status,
                          subscription_expires_at: result.data.subscription_expires_at
                        }))
                        dispatch({ type: "userLoggedIn", payload: {
                          name: result.data.name,
                          email: result.data.email,
                          subscription_type: result.data.subscription_type,
                          subscription_status: result.data.subscription_status,
                          subscription_expires_at: result.data.subscription_expires_at
                        }})
                        dispatch({
                          type: "newEvent",
                          value: {
                            header: "Abonament activat",
                            message: "Plata recurentă a fost activată cu succes!",
                            link: "/"
                          }
                        })
                        navigate('/v3.0')
                        window.location.reload()
                      } else {
                        throw new Error(result.message || 'Eroare la salvarea subscripției')
                      }
                    } catch (e) {
                      console.error('Eroare la salvarea subscripției:', e)
                      setError('Eroare la salvarea subscripției. Vă rugăm să contactați suportul.')
                      // Repornim timerul chiar și pe eroare, ca să nu rămână oprit
                      dispatch({ type: 'resetTimer' })
                    }
                  }}
                  onError={(err) => {
                    setError('Eroare la procesarea plății: ' + (err.message || ''))
                    setLoading(false)
                  }}
                />
              </PayPalScriptProvider>
            )}
          </div>


          <button
            type="button"
            className="v3-register-back"
            onClick={() => setRegistered(false)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Înapoi
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="v3-register-root">
      <div className="v3-register-container">
        <div className="v3-register-header">
          <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" className="v3-register-logo" />
          <h1>Înregistrare</h1>
          <p>Creează-ți contul pentru a continua</p>
        </div>

        <form onSubmit={handleNext} className="v3-register-form">
          {error && (
            <div className="v3-register-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <div className="v3-register-field">
            <label htmlFor="email">Email* <span className="v3-register-hint">(se va folosi ulterior la login)</span></label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemplu.com"
              required
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="password">Parola*</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="c_password">Confirma Parola*</label>
            <input
              id="c_password"
              type="password"
              value={c_password}
              onChange={(e) => setCPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="firstName">Prenume*</label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ion"
              required
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="lastName">Nume*</label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Popescu"
              required
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="phone">Telefon</label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0712345678"
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="judetSector">Judet/Sector</label>
            <select
              id="judetSector"
              value={judetSector}
              onChange={(e) => setJudetSector(e.target.value)}
              disabled={loading}
            >
              {JUDETE_SECTOARE.map(j => (
                <option key={j} value={j}>{j}</option>
              ))}
            </select>
          </div>

          <div className="v3-register-field">
            <label htmlFor="localitate">Localitate</label>
            <input
              id="localitate"
              type="text"
              value={localitate}
              onChange={(e) => setLocalitate(e.target.value)}
              placeholder="București"
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="adresa">Adresa</label>
            <input
              id="adresa"
              type="text"
              value={adresa}
              onChange={(e) => setAdresa(e.target.value)}
              placeholder="Str. Exemplu, Nr. 1"
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="compania">Compania</label>
            <input
              id="compania"
              type="text"
              value={compania}
              onChange={(e) => setCompania(e.target.value)}
              placeholder="Numele companiei"
              disabled={loading}
            />
          </div>

          <div className="v3-register-field">
            <label htmlFor="domeniuActivitate">Domeniu de activitate</label>
            <select
              id="domeniuActivitate"
              value={domeniuActivitate}
              onChange={(e) => setDomeniuActivitate(e.target.value)}
              disabled={loading}
            >
              {DOMENII_ACTIVITATE.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          <div className="v3-register-field">
            <label htmlFor="functia">Functia</label>
            <input
              id="functia"
              type="text"
              value={functia}
              onChange={(e) => setFunctia(e.target.value)}
              placeholder="Director, Manager, etc."
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="v3-register-submit"
            disabled={loading}
          >
            {loading ? 'Se procesează...' : 'Continuă'}
          </button>

          <div className="v3-register-links">
            <Link to="/v3.0/login" className="v3-register-link">
              Ai deja cont? Autentifică-te
            </Link>
          </div>
        </form>

        {!isGuestTimeout && (
          <button
            type="button"
            className="v3-register-back"
            onClick={() => navigate('/v3.0')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Înapoi la catalog
          </button>
        )}
      </div>
    </div>
  )
}

export default V3Register

